import React, { useEffect, useCallback } from 'react';
import { useSnackbar, VariantType, SnackbarMessage } from 'notistack';
import { useSelector, useDispatch } from 'react-redux';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';

import Icons from './Icons';
import { NotificationIcon } from '../../store/modules/notifications/types';
import { TimesIcon } from '../icon/solid';
import { selectNotificationsList } from '../../store/modules/notifications/selectors';
import uuid from '../../utility/uuid';
import { removeNotifications } from '../../store/modules/notifications/actions';

const makeTypeSet = (color: string) => ({
  color,
  background: '#FFF',
  borderBottomColor: color,
});

const useTypeStyles = makeStyles(({ palette }: Theme) => createStyles({
  default: makeTypeSet(palette.text.primary),
  success: makeTypeSet('#01B3C5'),
  error: makeTypeSet('#F8221A'),
  warning: makeTypeSet('#F8221A'),
  info: makeTypeSet('#2979FF'),
  custom: makeTypeSet('#2979FF'),
}), { name: 'NotificationType' });

const useStyles = makeStyles(({
  spacing, colors, breakpoints,
}: Theme) => createStyles({
  message: {
    display: 'flex',
    width: '350px',
    [breakpoints.down('xs')]: {
      maxWidth: '280px',
    },
    whiteSpace: 'pre-wrap',
  },
  icon: {
    fontSize: 20,
    marginRight: spacing(1),
    verticalAlign: 'middle',
  },
  closeIcon: {
    position: 'absolute',
    top: 4,
    right: 4,
    zIndex: 1000,
    height: 20,
    width: 20,
    borderRadius: '50%',
    color: colors.unselected,
    '&:hover': {
      cursor: 'pointer',
      color: colors.unselected,
    },
  },
  closeDefaultIcon: {
    color: '#FFF',
    '&:hover': {
      cursor: 'pointer',
      color: colors.unselected,
    },
  },
  action: {
    alignItems: 'end',
    height: '100%',
  },
}), { name: 'Notification' });

type Props = {
  message: SnackbarMessage;
  type: VariantType | 'custom';
  onClose: () => void;
  icon: NotificationIcon;
};

const NotificationElement = ({
  message, type, icon, onClose,
}: Props) => {
  const classes = useStyles();
  const typeClass = useTypeStyles()[type];
  const Icon = Icons[icon];

  return (
    <span className={classes.message}>
      {(type !== 'custom') && <Icon className={clsx(classes.icon, typeClass)} />}
      <span>
        {message}
      </span>
      <TimesIcon
        key="close"
        aria-label="close"
        color="inherit"
        className={clsx(classes.closeIcon, type === 'custom' ? classes.closeDefaultIcon : '')}
        onClick={onClose}
      />
    </span>
  );
};

const Notification = () => {
  const classes = useStyles();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const selectNotifications = useSelector(selectNotificationsList);
  const handleClick = useCallback((id: number | string) => () => closeSnackbar(id), [closeSnackbar]);

  useEffect(() => {
    const hasNotifications = selectNotifications.length !== 0;
    if (hasNotifications) {
      selectNotifications.forEach((notify) => {
        const id = uuid();
        const variant = notify.type === 'custom' ? 'default' : notify.type;
        const icon = notify.icon === undefined ? notify.type : notify.icon;
        enqueueSnackbar(
          <NotificationElement message={notify.message} type={notify.type} icon={icon} onClose={handleClick(id)} />,
          {
            key: id,
            variant,
          },
        );
      });
      dispatch(removeNotifications());
    }
  }, [dispatch, classes, enqueueSnackbar, handleClick, selectNotifications]);

  return (<></>);
};

export default Notification;
